import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, TestTube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/utils/api';

export default function AdminEmailSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: 'The Circuit'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get('/admin/smtp-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching SMTP settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.put('/admin/smtp-settings', settings);
      toast.success('SMTP settings saved! Email notifications will now work.');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Email Settings</h1>
            <p className="text-gray-600 mt-1">Configure SMTP for email notifications</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSave} className="bg-white rounded-lg shadow-md p-6 space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Email Service Setup</h3>
            <p className="text-sm text-blue-800">Configure your SMTP server to enable email notifications for new leads.</p>
            <p className="text-sm text-blue-800 mt-2"><strong>Popular providers:</strong></p>
            <ul className="text-sm text-blue-800 list-disc list-inside">
              <li>Gmail: smtp.gmail.com (Port 587 - TLS) - Use App Password</li>
              <li>Hostinger: smtp.hostinger.com (Port 465 - SSL)</li>
              <li>SendGrid: smtp.sendgrid.net (Port 587 - TLS)</li>
              <li>AWS SES: email-smtp.region.amazonaws.com (Port 587 - TLS)</li>
              <li>Outlook: smtp-mail.outlook.com (Port 587 - TLS)</li>
            </ul>
            <p className="text-sm text-blue-800 mt-2"><strong>Port Guide:</strong></p>
            <ul className="text-sm text-blue-800 list-disc list-inside">
              <li>Port 465: SSL/TLS (Hostinger, some providers)</li>
              <li>Port 587: STARTTLS (Gmail, most modern providers)</li>
            </ul>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>SMTP Host *</Label>
                <Input
                  value={settings.smtp_host}
                  onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                  placeholder="smtp.gmail.com"
                  required
                />
              </div>
              <div>
                <Label>SMTP Port *</Label>
                <Input
                  type="number"
                  value={settings.smtp_port}
                  onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) })}
                  placeholder="587"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>SMTP Username *</Label>
                <Input
                  value={settings.smtp_username}
                  onChange={(e) => setSettings({ ...settings, smtp_username: e.target.value })}
                  placeholder="your-email@gmail.com"
                  required
                />
              </div>
              <div>
                <Label>SMTP Password *</Label>
                <Input
                  type="password"
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder="Enter password or app password"
                  required={!settings.smtp_host}
                />
                <p className="text-xs text-gray-500 mt-1">For Gmail, use App Password (not account password)</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>From Email *</Label>
                <Input
                  type="email"
                  value={settings.smtp_from_email}
                  onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                  placeholder="noreply@thecircuit.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Email address that appears as sender</p>
              </div>
              <div>
                <Label>From Name</Label>
                <Input
                  value={settings.smtp_from_name}
                  onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                  placeholder="The Circuit"
                />
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800"><strong>Gmail Users:</strong></p>
            <ol className="text-sm text-yellow-800 list-decimal list-inside space-y-1 mt-2">
              <li>Go to Google Account → Security</li>
              <li>Enable 2-Step Verification</li>
              <li>Search for "App Passwords"</li>
              <li>Generate an app password for "Mail"</li>
              <li>Use that 16-character password here</li>
            </ol>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button
              type="submit"
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save SMTP Settings'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}